import { lazy, type FC, type ComponentProps } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { useClientState } from 'dibs-react-hooks/src/useClientState.ts';

import { PdpRespMarketplaceLoadingCarousel } from '../respMarketplace/PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type PdpSharedFeaturedRelatedItemsCarouselLazy_viewer$key } from './__generated__/PdpSharedFeaturedRelatedItemsCarouselLazy_viewer.graphql.ts';
import { type PdpSharedFeaturedRelatedItemsCarouselLazy_user$key } from './__generated__/PdpSharedFeaturedRelatedItemsCarouselLazy_user.graphql.ts';

const PdpSharedFeaturedRelatedItemsCarousel = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedFeaturedRelatedItemsCarousel'" */ './PdpSharedFeaturedRelatedItemsCarousel.tsx'
        )
);

type Props = {
    viewer: PdpSharedFeaturedRelatedItemsCarouselLazy_viewer$key;
    user: PdpSharedFeaturedRelatedItemsCarouselLazy_user$key | null | undefined;
    currency: string;
    onCarouselVisible?: () => void;
    carouselCapacity?: number;
    classOverrides?: ComponentProps<typeof PdpSharedFeaturedRelatedItemsCarousel>['classOverrides'];
};

const viewerFragment = graphql`
    fragment PdpSharedFeaturedRelatedItemsCarouselLazy_viewer on Viewer {
        ...PdpSharedFeaturedRelatedItemsCarousel_viewer
        sponsoredItems(
            userId: $userId
            guestId: $guestId
            itemId: $itemId
            isTrade: $isTrade
            priceBookName: $priceBookName
            userCountryCode: $userCountryCode
            pageSize: $numOfSponsoredItems
            maxItems: $maxSponsoredItems
            minItemsSteps: $minSponsoredItemsSteps
        ) {
            ...PdpSharedFeaturedRelatedItemsCarousel_sponsoredItems
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedFeaturedRelatedItemsCarouselLazy_user on User {
        ...PdpSharedFeaturedRelatedItemsCarousel_user
    }
`;

const PdpSharedFeaturedRelatedItemsCarouselLazyComponent: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    currency,
    onCarouselVisible,
    carouselCapacity,
    classOverrides,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const sponsoredItems = viewer?.sponsoredItems;

    return (
        <PdpSharedFeaturedRelatedItemsCarousel
            viewer={viewer}
            sponsoredItems={sponsoredItems}
            user={user}
            currency={currency}
            onCarouselVisible={onCarouselVisible}
            carouselCapacity={carouselCapacity}
            classOverrides={classOverrides}
        />
    );
};

export const PdpSharedFeaturedRelatedItemsCarouselLazy: FC<
    Props & { afterDisplay?: boolean | null }
> = ({
    viewer,
    user,
    currency,
    afterDisplay,
    onCarouselVisible,
    carouselCapacity,
    classOverrides,
}) => {
    const { isBot, isMobile } = useServerVarsContext();
    const isClient = useClientState();

    if (isBot) {
        return null;
    }

    const loadingComponent =
        !isMobile && isClient ? (
            <PdpRespMarketplaceLoadingCarousel titleClassName={classOverrides?.title} />
        ) : null;

    if (!afterDisplay) {
        return loadingComponent;
    }

    return (
        <ClientSuspense fallback={loadingComponent}>
            <PdpSharedFeaturedRelatedItemsCarouselLazyComponent
                viewer={viewer}
                user={user}
                currency={currency}
                onCarouselVisible={onCarouselVisible}
                carouselCapacity={carouselCapacity}
                classOverrides={classOverrides}
            />
        </ClientSuspense>
    );
};
